import * as Sentry from '@sentry/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { searchContextGraphNodes } from '@/services/context-graphs/search-context-graph-nodes';
import { useOrgStore } from '@/stores/org-store';

/**
 * Key prefix for a graph's node-search queries (all query strings). Lets
 * mutation paths patch/invalidate every cached search payload for the graph.
 */
export const contextGraphNodeSearchKey = (
  orgId: string | null,
  graphId: string | null,
) => ['context-graph-node-search', orgId, graphId] as const;

export const useContextGraphNodeSearch = (
  graphId: string | null,
  query: string,
  enabled = true,
) => {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid ?? null);
  const trimmed = query.trim();

  return useQuery({
    queryKey: [...contextGraphNodeSearchKey(orgId, graphId), trimmed],
    queryFn: async () => {
      try {
        return await searchContextGraphNodes(graphId as string, trimmed);
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'context-graph-node-search', orgId, graphId },
        });
        throw error;
      }
    },
    enabled: Boolean(enabled && orgId && graphId && trimmed.length >= 2),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
};
